import React, { useMemo, useState } from 'react';
import { AnimatePresence } from 'framer-motion';
import { Toolbar } from './src/components/Toolbar';
import { FilterSidebar } from './src/components/FilterSidebar';
import { DataTable } from './src/components/DataTable';
import { Header } from './src/components/Header';
import { DetailDrawer } from './src/components/DetailDrawer';
import { ComparisonView } from './src/components/ComparisonView';
import { VersionHistoryView } from './src/components/VersionHistoryView';
import { UploadIntake } from './src/components/UploadIntake';
import { LoginScreen } from './src/components/LoginScreen';
import { mockData, PARTIES, DiscoveryResponse, ResponseType, WorkflowStatus } from './src/data';
export function App() {
  const [isAuthenticated, setIsAuthenticated] = useState(false);
  // Filter states
  const [selectedParties, setSelectedParties] = useState<string[]>(PARTIES);
  const [selectedSets, setSelectedSets] = useState<string[]>([]);
  const [selectedTypes, setSelectedTypes] = useState<string[]>([]);
  const [selectedResponseTypes, setSelectedResponseTypes] = useState<ResponseType[]>([]);
  const [selectedWorkflowStatuses, setSelectedWorkflowStatuses] = useState<WorkflowStatus[]>([]);
  const [searchQuery, setSearchQuery] = useState('');
  const [selectedRow, setSelectedRow] = useState<DiscoveryResponse | null>(null);
  const [filtersOpen, setFiltersOpen] = useState(false);
  // View state
  const [currentView, setCurrentView] = useState<'workspace' | 'upload' | 'comparison' | 'versionHistory'>('workspace');
  const [comparisonInterrogatory, setComparisonInterrogatory] = useState<DiscoveryResponse | null>(null);
  const [versionHistoryRow, setVersionHistoryRow] = useState<DiscoveryResponse | null>(null);
  // Sorting state
  const [sortConfig, setSortConfig] = useState<{
    key: keyof DiscoveryResponse;
    direction: 'asc' | 'desc';
  } | null>({
    key: 'party',
    direction: 'asc'
  });
  const handleSort = (key: keyof DiscoveryResponse) => {
    let direction: 'asc' | 'desc' = 'asc';
    if (sortConfig && sortConfig.key === key && sortConfig.direction === 'asc') {
      direction = 'desc';
    }
    setSortConfig({
      key,
      direction
    });
  };
  const handleCompare = (row: DiscoveryResponse) => {
    setSelectedRow(null);
    setComparisonInterrogatory(row);
    setCurrentView('comparison');
  };
  const handleViewAllVersions = (row: DiscoveryResponse) => {
    setSelectedRow(null);
    setVersionHistoryRow(row);
    setCurrentView('versionHistory');
  };
  const handleBackToWorkspace = () => {
    setComparisonInterrogatory(null);
    setVersionHistoryRow(null);
    setCurrentView('workspace');
  };
  const hasActiveFilters = selectedSets.length > 0 || selectedTypes.length > 0 || selectedResponseTypes.length > 0 || selectedWorkflowStatuses.length > 0 || searchQuery.trim() !== '' || selectedParties.length !== PARTIES.length;
  const handleReset = () => {
    setSelectedParties(PARTIES);
    setSelectedSets([]);
    setSelectedTypes([]);
    setSelectedResponseTypes([]);
    setSelectedWorkflowStatuses([]);
    setSearchQuery('');
  };
  const filteredData = useMemo(() => {
    return mockData.filter(item => {
      if (selectedParties.length > 0 && !selectedParties.includes(item.party)) return false;
      if (selectedSets.length > 0 && !selectedSets.includes(item.set)) return false;
      if (selectedTypes.length > 0 && !selectedTypes.includes(item.type)) return false;
      if (selectedResponseTypes.length > 0) {
        const hasMatchingType = item.responseTypes.some(type => selectedResponseTypes.includes(type));
        if (!hasMatchingType) return false;
      }
      if (selectedWorkflowStatuses.length > 0 && !selectedWorkflowStatuses.includes(item.status)) {
        return false;
      }
      if (searchQuery.trim() !== '') {
        const query = searchQuery.toLowerCase();
        const searchableText = `${item.question} ${item.response} ${item.number} ${item.party}`.toLowerCase();
        if (!searchableText.includes(query)) return false;
      }
      return true;
    });
  }, [selectedParties, selectedSets, selectedTypes, selectedResponseTypes, selectedWorkflowStatuses, searchQuery]);
  const sortedData = useMemo(() => {
    const sortableItems = [...filteredData];
    if (sortConfig !== null) {
      sortableItems.sort((a, b) => {
        let aValue = a[sortConfig.key];
        let bValue = b[sortConfig.key];
        if (Array.isArray(aValue)) aValue = aValue.join(', ');
        if (Array.isArray(bValue)) bValue = bValue.join(', ');
        if (aValue < bValue) return sortConfig.direction === 'asc' ? -1 : 1;
        if (aValue > bValue) return sortConfig.direction === 'asc' ? 1 : -1;
        return 0;
      });
    }
    return sortableItems;
  }, [filteredData, sortConfig]);
  if (!isAuthenticated) {
    return <LoginScreen onLogin={() => setIsAuthenticated(true)} data-id="element-0" />;
  }
  return <div className="flex flex-col h-screen w-full overflow-hidden bg-geniac-bg font-sans text-geniac-text" data-id="element-1">
      <Header totalCount={mockData.length} filteredCount={filteredData.length} onUploadClick={() => setCurrentView('upload')} data-id="element-2" />

      {currentView === 'upload' ? <div className="flex-1 overflow-hidden" data-id="element-3">
          <UploadIntake onBack={handleBackToWorkspace} data-id="element-4" />
        </div> : currentView === 'comparison' && comparisonInterrogatory ? <div className="flex-1 overflow-hidden" data-id="element-5">
          <ComparisonView interrogatory={comparisonInterrogatory} onBack={handleBackToWorkspace} data-id="element-6" />
        </div> : currentView === 'versionHistory' && versionHistoryRow ? <div className="flex-1 overflow-hidden" data-id="element-7">
          <VersionHistoryView row={versionHistoryRow} onBack={handleBackToWorkspace} data-id="element-8" />
        </div> : <>
          <Toolbar filtersOpen={filtersOpen} setFiltersOpen={setFiltersOpen} hasActiveFilters={hasActiveFilters} data-id="element-9" />
          <div className="flex-1 overflow-hidden flex" data-id="element-10">
            <AnimatePresence data-id="element-11">
              {filtersOpen && <FilterSidebar selectedParties={selectedParties} setSelectedParties={setSelectedParties} selectedSets={selectedSets} setSelectedSets={setSelectedSets} selectedTypes={selectedTypes} setSelectedTypes={setSelectedTypes} selectedResponseTypes={selectedResponseTypes} setSelectedResponseTypes={setSelectedResponseTypes} selectedWorkflowStatuses={selectedWorkflowStatuses} setSelectedWorkflowStatuses={setSelectedWorkflowStatuses} searchQuery={searchQuery} setSearchQuery={setSearchQuery} onReset={handleReset} hasActiveFilters={hasActiveFilters} data-id="element-12" />}
            </AnimatePresence>

            <main className="flex-1 overflow-hidden bg-geniac-bg flex flex-col min-w-0" data-id="element-13">
              <DataTable data={sortedData} sortConfig={sortConfig} onSort={handleSort} onRowClick={setSelectedRow} selectedRowId={selectedRow?.id} data-id="element-14" />
            </main>

            <AnimatePresence data-id="element-15">
              {selectedRow && <DetailDrawer row={selectedRow} onClose={() => setSelectedRow(null)} onCompare={handleCompare} onViewAllVersions={handleViewAllVersions} data-id="element-16" />}
            </AnimatePresence>
          </div>
        </>}
    </div>;
}